import { types as api } from '@mesa-labs/mesa-api';
import { LoadingSpinner } from '@mesa-labs/mesa-ui';
import React, { useState } from 'react';
import styled from 'styled-components';

import { useAddVendorNoteMutation, useGetVendorNotesQuery } from '../../../../redux/api/vendors';
import { ActionItemActions, useWriteAttempt } from '../hooks';
import ActionItemTimeline from './ActionItemTimeline';
import InlineCreateActionItem from './InlineCreateActionItem';
import InlineEntryForm, { Select } from './InlineEntryForm';
import Linkified from './Linkified';
import {
  LinkButton, Muted, SectionHeader, SectionLabel,
} from './styles';
import VendorActionItemsSection from './VendorActionItemsSection';
import { formatDateTime } from '../../../../utils';
import { formatAssigneeEmail } from '../../../../utils/vendorActionItems';
import { noteTypeLabel, NoteTypeOptions } from '../../../../utils/vendorNotes';

// Sections split by a rule, matching the drawer's #EAECF0 borders.
const Sections = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  & > * + * {
    border-top: 1px solid #EAECF0;
    padding-top: 20px;
  }
`;

// Plain divs, not ul/li: as list items, each Note rendered with a stray "1" above it.
const NoteList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const NoteMeta = styled.div`
  align-items: center;
  color: ${(p) => p.theme.colors.Gray500};
  display: flex;
  font-size: 11px;
  gap: 8px;
  margin-bottom: 2px;
`;

// Long Notes clamp to a few lines; the full text is on hover and on the Vendor's Notes page.
const NoteText = styled.div`
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  color: #101828;
  display: -webkit-box;
  font-size: 13px;
  overflow: hidden;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
`;

const ShowMore = styled(LinkButton)`
  margin-top: 8px;
`;

const RECENT_COMMENTS_LIMIT = 5;
const RECENT_NOTES_LIMIT = 3;

type ShowMoreToggleProps = {
  total: number;
  limit: number;
  expanded: boolean;
  onToggle: () => void;
};

// Under a list capped at `limit`: expands it in place ("Show N more") and collapses it again ("Show less").
function ShowMoreToggle({
  total, limit, expanded, onToggle,
}: ShowMoreToggleProps): React.ReactElement | null {
  if (total <= limit) return null;
  return <ShowMore type="button" onClick={onToggle}>{expanded ? 'Show less' : `Show ${total - limit} more`}</ShowMore>;
}

const NoNotes: readonly api.VendorNoteResponse[] = [];

type Props = {
  item: api.VendorActionItemResponse;
  // The item's comments, newest first; undefined while the timeline loads.
  comments?: readonly api.VendorActionItemTimelineEntry[];
  commentsLoading: boolean;
  me?: string;
  operators: readonly api.VendorActionItemAssigneeResponse[];
  canWrite: boolean;
  actions: ActionItemActions;
  // Opens another of the Vendor's items in the drawer.
  onOpenItem: (id: string) => void;
};

// The item's comments with an always-open one-line form, then the Vendor's recent Notes and other Action Items, each
// with a "+ New" one. Action Items are created for the same Vendor (`actions` is bound to it), so a new item shows up
// here without leaving the one being viewed.
export default function ActionItemContext({
  item, comments, commentsLoading, me, operators, canWrite, actions, onOpenItem,
}: Props): React.ReactElement {
  const [showAllComments, setShowAllComments] = useState(false);
  const [showAllNotes, setShowAllNotes] = useState(false);
  const [addingNote, setAddingNote] = useState(false);
  const [noteType, setNoteType] = useState(api.VendorNoteType.GENERAL);
  const [creatingItem, setCreatingItem] = useState(false);
  const { data: notes = NoNotes, isLoading: isLoadingNotes } = useGetVendorNotesQuery({ vendorId: item.vendorId });
  const [addVendorNote] = useAddVendorNoteMutation();
  const noteWrite = useWriteAttempt();
  const shownComments = comments && !showAllComments ? comments.slice(0, RECENT_COMMENTS_LIMIT) : comments;
  const shownNotes = showAllNotes ? notes : notes.slice(0, RECENT_NOTES_LIMIT);

  const toggleAddingNote = () => {
    noteWrite.clearError();
    setAddingNote(!addingNote);
  };

  const toggleCreatingItem = () => {
    actions.clearError();
    setCreatingItem(!creatingItem);
  };

  const addNote = async (note: string) => {
    const saved = await noteWrite.attempt(() => addVendorNote({ vendorId: item.vendorId, note, noteType }).unwrap(), 'There was an error adding the Note') !== undefined;
    if (saved) setAddingNote(false);
    return saved;
  };

  return (
    <Sections>
      <div>
        <SectionHeader>
          <SectionLabel>{`Comments${comments?.length ? ` (${comments.length})` : ''}`}</SectionLabel>
        </SectionHeader>
        {/* Always open, so it doesn't take focus when the drawer opens. */}
        {canWrite && <InlineEntryForm autoFocus={false} placeholder="Add a comment for this action item" submitText="Post" onSubmit={(text) => actions.addNote(item.id, text)} />}
        {!shownComments && commentsLoading && <LoadingSpinner height="18px" />}
        {shownComments && (shownComments.length > 0 ? <ActionItemTimeline entries={shownComments} /> : <Muted>No comments yet</Muted>)}
        <ShowMoreToggle
          total={comments?.length ?? 0}
          limit={RECENT_COMMENTS_LIMIT}
          expanded={showAllComments}
          onToggle={() => setShowAllComments(!showAllComments)}
        />
      </div>

      <div>
        <SectionHeader>
          <SectionLabel>{`Vendor Notes${notes.length > 0 ? ` (${notes.length})` : ''}`}</SectionLabel>
          {canWrite && <LinkButton type="button" onClick={toggleAddingNote}>{addingNote ? 'Cancel' : '+ New'}</LinkButton>}
        </SectionHeader>
        {addingNote && (
          <InlineEntryForm
            placeholder="Add a new vendor note"
            submitText="Save"
            error={noteWrite.error}
            select={(
              <Select aria-label="Note type" value={noteType} onChange={(e) => setNoteType(e.target.value as api.VendorNoteType)}>
                {NoteTypeOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </Select>
            )}
            onSubmit={addNote}
          />
        )}
        {!isLoadingNotes && notes.length === 0 && <Muted>No notes yet</Muted>}
        <NoteList>
          {shownNotes.map((note) => (
            <div key={note.id}>
              <NoteMeta>
                {`${noteTypeLabel(note.noteType)} · ${formatAssigneeEmail(note.createdBy)} · ${formatDateTime(note.createdAt)}`}
              </NoteMeta>
              <NoteText title={note.note}><Linkified text={note.note} /></NoteText>
            </div>
          ))}
        </NoteList>
        <ShowMoreToggle total={notes.length} limit={RECENT_NOTES_LIMIT} expanded={showAllNotes} onToggle={() => setShowAllNotes(!showAllNotes)} />
      </div>

      <VendorActionItemsSection
        vendorId={item.vendorId}
        excludeId={item.id}
        label="Related Action Items"
        me={me}
        canWrite={canWrite}
        onOpen={(related) => onOpenItem(related.id)}
        action={canWrite && <LinkButton type="button" onClick={toggleCreatingItem}>{creatingItem ? 'Cancel' : '+ New'}</LinkButton>}
      >
        {creatingItem && (
          <InlineCreateActionItem
            me={me}
            operators={operators}
            onCreate={async (request) => {
              if (await actions.create(request)) setCreatingItem(false);
            }}
          />
        )}
      </VendorActionItemsSection>
    </Sections>
  );
}
